import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MailCheck, RotateCw, ShieldCheck } from "lucide-react";

export const CodeInput = ({ value, onChange, testId }) => (
  <InputOTP maxLength={6} value={value} onChange={onChange} autoFocus data-testid={testId}>
    <InputOTPGroup>
      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}
    </InputOTPGroup>
  </InputOTP>
);

export const TwoFactorStep = ({ challenge, onVerify, onCancel }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Saisissez les 6 chiffres reçus par email");
    setLoading(true);
    setError("");
    try {
      await onVerify(challenge.challenge_id, code);
    } catch (err) {
      setError(formatApiError(err, "Code incorrect"));
      setCode("");
      if (err?.response?.status === 429 || /Recommencez/.test(err?.response?.data?.detail || "")) {
        toast.error(formatApiError(err, "Trop de codes incorrects, recommencez la connexion"));
        onCancel();
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.resendTwoFactorCode(challenge.challenge_id);
      toast.success("Nouveau code envoyé");
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de renvoyer le code"));
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="two-factor-step">
      <div className="flex gap-3 rounded-md border bg-muted/40 p-4 text-sm">
        <MailCheck className="h-5 w-5 text-primary shrink-0" />
        <p>Un code à 6 chiffres vient d'être envoyé {challenge.channel === "sms" ? "par SMS au" : "à"} <strong data-testid="two-factor-email-hint">{challenge.email_hint}</strong>. Il est valable 10 minutes.</p>
      </div>
      <div className="flex justify-center">
        <CodeInput value={code} onChange={setCode} testId="two-factor-code-input" />
      </div>
      {error && <p className="text-sm text-destructive text-center" data-testid="two-factor-error">{error}</p>}
      <Button type="submit" className="w-full h-11" disabled={loading} data-testid="two-factor-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
        Valider le code
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onCancel} className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
          data-testid="two-factor-back-btn">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <button type="button" onClick={resend} disabled={resending} className="flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
          data-testid="two-factor-resend-btn">
          <RotateCw className={`h-4 w-4 ${resending ? "animate-spin" : ""}`} /> Renvoyer le code
        </button>
      </div>
    </form>
  );
};
